// TradesCell.jsx — compact trade-chips cell with a "view all" popup.
//
// Reusable anywhere a record exposes a list of trades. Keeps table rows
// tidy by showing only the first few trades inline and moving the full
// list into a themed, dark-mode-safe modal.
//
// Usage:
//   <TradesCell trades={row.trade_of_works} />
//   <TradesCell trades={row.trade_of_works} maxVisible={3} title="Trades" />

import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Briefcase, X, Layers } from "lucide-react";
import { formatTradeLabel } from "utils/trade-format";

const TradeChip = ({ trade }) => {
  const label = formatTradeLabel(trade);
  return (
    <span
      className="inline-flex items-center max-w-[140px] truncate px-1 py-px rounded bg-surface_alt text-text_secondary border border-border text-[10px] leading-tight font-medium whitespace-nowrap"
      title={label}
    >
      {label}
    </span>
  );
};

const TradesDialog = ({ trades, title, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[2050] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/45 backdrop-blur-[2px]"
        onClick={onClose}
      />

      {/* Card */}
      <div className="relative w-full max-w-md bg-surface border border-border rounded-xl overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.18)]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-primary">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/15 text-white">
              <Briefcase size={16} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white">{title}</h3>
              <p className="text-[11px] text-white/70">
                {trades.length} trade{trades.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="max-h-[60vh] overflow-y-auto p-4 space-y-2">
          {trades.map((trade) => (
            <div
              key={trade.id}
              className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border border-border bg-surface_alt"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-primary_tint text-primary">
                  <Layers size={14} />
                </div>
                <span className="text-[13px] font-medium text-text_primary truncate">
                  {trade.trade_name}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
};

const TradesCell = ({ trades, maxVisible = 2, title = "Trade of Work" }) => {
  const [open, setOpen] = useState(false);
  const list = Array.isArray(trades) ? trades : [];

  if (list.length === 0) {
    return <span className="text-xs text-text_muted italic">N/A</span>;
  }

  const visible = list.slice(0, maxVisible);
  const remaining = list.length - visible.length;

  return (
    <>
      <div className="flex flex-wrap items-center gap-1 min-w-0">
        {visible.map((trade) => (
          <TradeChip key={trade.id} trade={trade} />
        ))}

        {remaining > 0 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(true);
            }}
            className="flex-shrink-0 inline-flex items-center px-1 py-px rounded bg-primary_tint text-primary border border-border text-[10px] leading-tight font-medium hover:bg-primary/15 transition-colors"
            title="View all trades"
          >
            +{remaining} more
          </button>
        )}
      </div>

      {open && (
        <TradesDialog
          trades={list}
          title={title}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};

export default TradesCell;
